"use client";

import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import jwt_decode from "jwt-decode";
import { useAxiosFetch } from "../../hooks/useAxiosFetch";
import { axiosRequest } from "../../utility/axios";
import ChipsMaster from "../chipsMaster";
import { Heading } from "../paymentOffer/paymentOffer";
import { getChipColor } from "../../components/ChipToken";
import { apiBaseUrl } from "../../constant/endpoint";
import { ACCESS_LEVEL } from "../../constant/constant";

export default function Shift() {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;
  const isMainAdmin = (() => { try { return jwt_decode(token)?.accessLevel >= 100; } catch { return false; } })();
  const isCashier = (() => { try { return jwt_decode(token)?.accessLevel === ACCESS_LEVEL.CASHIER; } catch { return false; } })();
  const { data, refetch } = useAxiosFetch("GET", "/chips", token);
  const { data: tourChip, refetch: tourRefetch } = useAxiosFetch("GET", "/tournament_chips", token);

  // Deduplicate chips by denomination — keep highest quantity per denomination
  const rawChips = data || [];
  const chipDenomsMap = {};
  rawChips.forEach(c => {
    const d = c.denomination;
    if (!chipDenomsMap[d] || c.quantity > chipDenomsMap[d].quantity) chipDenomsMap[d] = c;
  });
  const chips = Object.values(chipDenomsMap).sort((a, b) => a.denomination - b.denomination);
  const tournamentChips = tourChip || [];
  const totalValue = chips.reduce(
    (sum, chip) => sum + chip.denomination * chip.quantity,
    0
  );
  const tourTotalVaue = tournamentChips.reduce(
    (sum, chip) => sum + chip.denomination * chip.quantity,
    0
  );

  const [shiftActive, setShiftActive] = useState(false);
  const [openingCash, setOpeningCash] = useState(0);
  const [openingBank, setOpeningBank] = useState(0);
  const [currTableChip, setCurrTableChip] = useState({});
  const [currTourTableChip, setCurrTourTableChip] = useState({});

  const [showStartForm, setShowStartForm] = useState(false);
  const [formOpeningCash, setFormOpeningCash] = useState("");
  const [formChipQty, setFormChipQty] = useState({});
  const [userWallet, setUserWallet] = useState(null);
  const [issueSummary, setIssueSummary] = useState(null);
  const [currentShift, setCurrentShift] = useState(null);
  const [shiftSummary, setShiftSummary] = useState(null);
  const [refillOpen, setRefillOpen] = useState(false);
  const [refillCash, setRefillCash] = useState("");
  const [refillChipQty, setRefillChipQty] = useState({});
  const [refillNote, setRefillNote] = useState("");
  const [refillSubmitting, setRefillSubmitting] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closeShiftLoading, setCloseShiftLoading] = useState(false);
  const [tgShifts, setTgShifts] = useState([]);
  const [selectedTgCashiers, setSelectedTgCashiers] = useState([]);
  const [tgStartDialog, setTgStartDialog] = useState(null); // { cashier_id, cashier_name }
  const [tgOpeningCash, setTgOpeningCash] = useState("");
  const [tgOpeningChips, setTgOpeningChips] = useState("");
  const [tgStartLoading, setTgStartLoading] = useState(false);
  const [tgRefillDialog, setTgRefillDialog] = useState(null); // { cashier_id, cashier_name }
  const [tgRefillCash, setTgRefillCash] = useState("");
  const [tgRefillChips, setTgRefillChips] = useState("");
  const [tgRefillNote, setTgRefillNote] = useState("");
  const [tgRefillLoading, setTgRefillLoading] = useState(false);

  /* ---------------- FETCH VAULT ---------------- */

  useEffect(() => {
    getVault();
    getUserWallet();
    fetchIssueSummary();
    fetchCurrentShift();
    fetchShiftSummary();
    fetchTgShifts();

    const interval = setInterval(() => {
      fetchShiftSummary();
      getUserWallet();
      fetchTgShifts();
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const fetchTgShifts = async () => {
    try {
      const res = await axiosRequest("GET", "/shift-management/tg-shifts", token);
      if (res?.data) setTgShifts(res.data);
    } catch (e) { console.log("tg-shifts fetch error:", e); }
  };

  const getUserWallet = async () => {
    try {
      const res = await axiosRequest("GET", "/current-user-wallet", token);
      if (res?.data) setUserWallet(res.data);
    } catch (error) {
      console.log("Wallet fetch error:", error);
    }
  };

  const fetchIssueSummary = async () => {
    try {
      const res = await axiosRequest("GET", "/shift-management/issue-summary", token);
      if (res?.data) setIssueSummary(res.data);
    } catch (error) {
      console.log("Issue summary fetch error:", error);
    }
  };

  const fetchCurrentShift = async () => {
    try {
      const res = await axiosRequest("GET", "/shift-management/current", token);
      if (res?.data) setCurrentShift(res.data);
    } catch (error) {
      console.log("Current shift fetch error:", error);
    }
  };

  const fetchShiftSummary = async () => {
    try {
      const res = await axiosRequest("GET", "/shift-management/current-summary", token);
      if (res?.data) setShiftSummary(res.data);
    } catch (error) {
      console.log("Shift summary fetch error:", error);
    }
  };

  const getVault = async () => {
    try {
      const res = await axiosRequest(
        "GET",
        "/vault",
        token
      );


      setOpeningCash(res?.data?.cash || 0);
      setOpeningBank(res?.data?.bank || 0);
      setShiftActive(res?.data?.shift == "OPEN" ? true : false);
      setCurrTableChip(res?.data?.chip_denominations);
      setCurrTourTableChip(res?.data?.chip_denominations_tournament);
      console.log("/vault", res?.data);
    } catch (error) {
      console.log("Vault fetch error:", error);
    }
  };

  /* ---------------- SHIFT OPEN ---------------- */

  const handleStartShiftClick = async () => {
    const initialChipQty = {};
    chips.forEach((chip) => { initialChipQty[chip.denomination] = ""; });

    // Pre-fill with previous shift's closing values
    try {
      const res = await axiosRequest("GET", "/shift-management/last-closed", token);
      if (res?.data) {
        setFormOpeningCash(String(Math.max(0, Math.round(res.data.closing_cash || 0))));
        const closingDenoms = res.data.closing_chip_denominations || {};
        chips.forEach((chip) => {
          initialChipQty[chip.denomination] = closingDenoms[chip.denomination] != null
            ? String(closingDenoms[chip.denomination])
            : "";
        });
      } else {
        setFormOpeningCash("");
      }
    } catch {
      setFormOpeningCash("");
    }

    setFormChipQty(initialChipQty);
    setShowStartForm(true);
  };

  const totalFormCash = Number(formOpeningCash) || 0;

  const totalFormChips = chips.reduce(
    (sum, chip) => sum + chip.denomination * (Number(formChipQty[chip.denomination]) || 0), 0
  );

  const handleStartShift = async () => {
    try {
      const chipDenominations = Object.fromEntries(
        Object.entries(formChipQty).map(([d, q]) => [d, Number(q) || 0])
      );

      await axiosRequest("PUT", "/shift", token, { type: "OPEN" });

      await axiosRequest("POST", "/shift-management", token, {
        opening_cash: totalFormCash,
        opening_chips: totalFormChips,
        chip_denominations: chipDenominations,
        winning_balance: userWallet?.winning_balance || 0,
        promotional_balance: userWallet?.promotional_balance || 0,
        cash_deposite: userWallet?.cash_deposite || 0,
        loan_distributed: userWallet?.loan_distributed || 0,
        float_outstanding: userWallet?.float_outstanding || 0,
      });

      // Start selected Telegram cashier shifts (opening values collected individually via dialog)
      await Promise.all(
        selectedTgCashiers.map(id =>
          axiosRequest("POST", "/shift-management/tg-shifts/start", token, {
            cashier_id: id,
            opening_cash: 0,
            opening_chips: 0,
          })
        )
      );
      setSelectedTgCashiers([]);
      setShiftActive(true);
      setShowStartForm(false);
      fetchCurrentShift();
      fetchIssueSummary();
      fetchShiftSummary();
      fetchTgShifts();
    } catch (error) {
      console.log("Shift open error:", error);
    }
  };

  /* ---------------- SHIFT CLOSE ---------------- */

  const handleCloseShift = () => setCloseConfirmOpen(true);

  const handleConfirmCloseShift = async () => {
    setCloseShiftLoading(true);
    try {
      await axiosRequest("PUT", "/shift", token, { type: "CLOSE" });
      setShiftActive(false);
      setCloseConfirmOpen(false);
    } catch (error) {
      console.log("Shift close error:", error);
    } finally {
      setCloseShiftLoading(false);
    }
  };

  /* ---------------- REFILL ---------------- */

  const handleOpenRefill = () => {
    const init = {};
    chips.forEach((c) => { init[c.denomination] = ""; });
    setRefillChipQty(init);
    setRefillCash("");
    setRefillNote("");
    setRefillOpen(true);
  };

  const refillTotalChips = chips.reduce(
    (s, c) => s + c.denomination * (Number(refillChipQty[c.denomination]) || 0), 0
  );

  const handleSubmitRefill = async () => {
    if (!refillCash && refillTotalChips === 0) return;
    setRefillSubmitting(true);
    const chipDenominations = Object.fromEntries(
      Object.entries(refillChipQty).map(([d, q]) => [d, Number(q) || 0])
    );
    await axiosRequest("POST", "/refill", token, {
      cash_amount: Number(refillCash) || 0,
      chip_denominations: chipDenominations,
      note: refillNote,
    });
    setRefillSubmitting(false);
    setRefillOpen(false);
    fetchShiftSummary();
    getVault();
  };

  const handleDownloadRefillCsv = async (shiftId) => {
    const url = `${apiBaseUrl}/refill/export-csv${shiftId ? `?shift_id=${shiftId}` : ""}`
    const res = await fetch(url, { headers: { token, auth_token: token } })
    if (!res.ok) return
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = objectUrl
    a.download = `refill_history_${shiftId || Date.now()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(objectUrl), 100)
  }

  /* ---------------- UI ---------------- */

  if (isCashier) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
        <Typography variant="h4" fontWeight={600} mb={3}>
          Shift Management
        </Typography>
        <Paper elevation={4} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Available on Counter</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Paper sx={{ p: 2, background: "#0f2a17", borderRadius: 2, textAlign: "center" }}>
                <Typography variant="h5" fontWeight={700} color="success.dark" mt={0.5}>
                  {((shiftSummary?.counter_cash?.total || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Paper sx={{ p: 2, background: "#0f2740", borderRadius: 2, textAlign: "center" }}>
                <Typography variant="h5" fontWeight={700} color="primary.dark" mt={0.5}>
                  {(shiftSummary?.counter_chips?.total || 0).toLocaleString()}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Typography variant="h4" fontWeight={600} mb={3}>
        Shift Management
      </Typography>

      <Paper elevation={4} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
        {/* SHIFT STATUS */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6">
            Current Shift Status
          </Typography>

          <Chip
            label={shiftActive ? "ACTIVE" : "CLOSED"}
            color={shiftActive ? "success" : "error"}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ---------------- SHIFT OPENING DETAILS (fixed snapshot) ---------------- */}

        {currentShift && Object.keys(currentShift).length > 0 && (
          <>
            <Typography variant="h6" fontWeight={600} mb={2}>Shift Opening Details</Typography>
            <Grid container spacing={2} mb={2}>
              {[
                { label: "Started By",           value: currentShift.email || "—", isText: true },
                { label: "Date & Time",           value: currentShift.date_time ? new Date(currentShift.date_time).toLocaleString() : "—", isText: true },
                { label: "Opening Cash",          value: `${(currentShift.opening_cash || 0).toLocaleString()}` },
                { label: "Opening Chips",         value: `${(currentShift.opening_chips || 0).toLocaleString()}` },
                { label: "Winning Balance",       value: `${(currentShift.winning_balance || 0).toLocaleString()}` },
                { label: "Promotional Balance",   value: `${(currentShift.promotional_balance || 0).toLocaleString()}` },
                { label: "Cash Deposited",        value: `${(currentShift.cash_deposite || 0).toLocaleString()}` },
                { label: "Loan Distributed",      value: `${(currentShift.loan_distributed || 0).toLocaleString()}` },
                { label: "Float Outstanding",     value: `${(currentShift.float_outstanding || 0).toLocaleString()}` },
              ].map(({ label, value }) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Paper sx={{ p: 2, background: "#1c2433", borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="subtitle1" fontWeight={600} mt={0.5}>{value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Chip Denominations */}
            {currentShift.chip_denominations && Object.keys(currentShift.chip_denominations).length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Chip Denominations</Typography>
                <Grid container spacing={1}>
                  {Object.entries(currentShift.chip_denominations).map(([d, q]) => {
                    const cc = getChipColor(d);
                    return (
                    <Grid item key={d}>
                      <Paper sx={{ px: 2, py: 1, background: cc.bg, borderRadius: 2, textAlign: "center" }}>
                        <Typography variant="caption" sx={{ color: cc.text, opacity: 0.8 }}>{d}</Typography>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: cc.text }}>{q} pcs</Typography>
                        <Typography variant="caption" sx={{ color: cc.text, opacity: 0.8 }}>= {(d * q).toLocaleString()}</Typography>
                      </Paper>
                    </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* ---------------- CURRENT DETAILS (live transactions) — only when shift is active ---------------- */}

        {shiftActive && shiftSummary && (
          <>
            <Typography variant="h6" fontWeight={600} mb={2}>Shift Details</Typography>
            <Grid container spacing={2} mb={2}>
              {[
                    { label: "Deposit",       key: "deposit",       color: "#e8f5e9" },
                    { label: "Chip Issue",    key: "chip_issue",    color: "#e3f2fd" },
                    { label: "Chip Cash-Out", key: "chip_cashout",  color: "#fff3e0" },
                    { label: "Chip Exchange", key: "chip_exchange", color: "#f3e5f5" },
                    { label: "Withdrawal",    key: "withdrawal",    color: "#fce4ec" },
                    { label: "Rake",          key: "rake",          color: "#ede7f6" },
                    { label: "Expense",           key: "expense",           color: "#ffebee" },
                    { label: "Offer Distributed", key: "offer_distributed", color: "#e8eaf6" },
                    { label: "Float Issued",      key: "float_issued",      color: "#2e2008" },
                    { label: "Float Repaid",      key: "float_repaid",      color: "#0f2a17" },
                  ].map(({ label, key, color }) => (
                    <Grid item xs={12} sm={6} md={2} key={key}>
                      <Paper sx={{ p: 2, background: color, borderRadius: 2, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="subtitle1" fontWeight={700} mt={0.5}>
                          {(shiftSummary[key]?.total || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {shiftSummary[key]?.count || 0} {key === "rake" ? "records" : "txns"}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
            </Grid>

            {/* Float exchange — outstanding across all shifts */}
            {(shiftSummary.float_outstanding?.total > 0 || shiftSummary.float_carried_in?.total > 0) && (
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, background: "#2e2008", borderRadius: 2, textAlign: "center", borderLeft: "4px solid #ffb74d" }}>
                    <Typography variant="caption" color="text.secondary">Float Outstanding</Typography>
                    <Typography variant="subtitle1" fontWeight={700} mt={0.5} color="#ffb74d">
                      {(shiftSummary.float_outstanding?.total || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{shiftSummary.float_outstanding?.count || 0} players</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, background: "#1c2433", borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">Float Carried In (earlier shifts)</Typography>
                    <Typography variant="subtitle1" fontWeight={700} mt={0.5}>
                      {(shiftSummary.float_carried_in?.total || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{shiftSummary.float_carried_in?.count || 0} players</Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* Rake chip denomination breakdown */}
            {shiftSummary.rake?.chips && Object.keys(shiftSummary.rake.chips).length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Rake Chips Collected</Typography>
                <Grid container spacing={1}>
                  {Object.entries(shiftSummary.rake.chips).map(([d, q]) => (
                    <Grid item key={d}>
                      <Paper sx={{ px: 2, py: 1, background: "#211a35", borderRadius: 2, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">{d}</Typography>
                        <Typography variant="subtitle2" fontWeight={700}>{q} pcs</Typography>
                        <Typography variant="caption">= {(d * q).toLocaleString()}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* ---------------- AVAILABLE ON COUNTER ---------------- */}
            <Typography variant="h6" fontWeight={600} mb={2}>Available on Counter</Typography>

            {/* Summary totals */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, background: "#0f2a17", borderRadius: 2, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">Available Cash</Typography>
                  <Typography variant="h5" fontWeight={700} color="success.dark" mt={0.5}>
                    {(shiftSummary.counter_cash?.total || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, background: "#fff8e1", borderRadius: 2, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">Available Bank</Typography>
                  <Typography variant="h5" fontWeight={700} color="warning.dark" mt={0.5}>
                    {(shiftSummary.counter_bank?.total || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, background: "#0f2740", borderRadius: 2, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">Available Chips</Typography>
                  <Typography variant="h5" fontWeight={700} color="primary.dark" mt={0.5}>
                    {(shiftSummary.counter_chips?.total || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Available chips denomination breakdown */}
            {shiftSummary.counter_chips?.denominations && Object.keys(shiftSummary.counter_chips.denominations).length > 0 && (
              <Box mb={3}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Chips on Counter (by Denomination)</Typography>
                <Grid container spacing={1}>
                  {Object.entries(shiftSummary.counter_chips.denominations)
                    .sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([d, q]) => {
                      const cc = getChipColor(d);
                      return (
                      <Grid item key={d}>
                        <Paper sx={{ px: 2, py: 1, background: cc.bg, borderRadius: 2, textAlign: "center", minWidth: 90 }}>
                          <Typography variant="caption" sx={{ color: cc.text, opacity: 0.8 }}>{d}</Typography>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: cc.text }}>{q} pcs</Typography>
                          <Typography variant="caption" sx={{ color: cc.text, opacity: 0.8 }}>= {(d * q).toLocaleString()}</Typography>
                        </Paper>
                      </Grid>
                      );
                    })}
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* ---------------- START SHIFT ---------------- */}

        {!shiftActive && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Start New Shift
            </Typography>

            {/* Current User Wallet Balances */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Winning Balance</Typography>
                  <Typography variant="h6" fontWeight={700} mt={0.5}>{(userWallet?.winning_balance || 0).toLocaleString()}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Promotional Balance</Typography>
                  <Typography variant="h6" fontWeight={700} mt={0.5}>{(userWallet?.promotional_balance || 0).toLocaleString()}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Cash Deposited</Typography>
                  <Typography variant="h6" fontWeight={700} mt={0.5}>{(userWallet?.cash_deposite || 0).toLocaleString()}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Loan Distributed</Typography>
                  <Typography variant="h6" fontWeight={700} mt={0.5}>{(userWallet?.loan_distributed || 0).toLocaleString()}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Float Outstanding</Typography>
                  <Typography variant="h6" fontWeight={700} mt={0.5}>{(userWallet?.float_outstanding || 0).toLocaleString()}</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Telegram Cashier Selection on Start Shift */}
            {tgShifts.filter(s => s.is_active).length > 0 && (
              <Box mb={3}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Select Telegram Cashiers for this shift
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Grid container spacing={1}>
                    {tgShifts.filter(s => s.is_active).map((s) => {
                      const id = String(s.cashier_id);
                      const checked = selectedTgCashiers.includes(id);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={(e) => setSelectedTgCashiers(prev =>
                                  e.target.checked ? [...prev, id] : prev.filter(x => x !== id)
                                )}
                                color="primary"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{s.cashier_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{s.cashier_phone}</Typography>
                                {s.shift_active && (
                                  <Chip label="Shift already running" size="small" color="warning" sx={{ ml: 1, fontSize: "0.65rem" }} />
                                )}
                              </Box>
                            }
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                  {selectedTgCashiers.length > 0 && (
                    <Typography variant="caption" color="primary" mt={1} display="block">
                      ✓ {selectedTgCashiers.length} cashier(s) will be started with this shift
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            {!showStartForm ? (
              <Button variant="contained" size="large" onClick={handleStartShiftClick}>
                Start Shift
              </Button>
            ) : (
              <Box sx={{ mt: 2 }}>
                {/* Opening Cash Amount */}
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Opening Cash Amount
                </Typography>
                <TextField
                  label="Opening Cash Amount ()"
                  type="number"
                  value={formOpeningCash}
                  onChange={(e) => setFormOpeningCash(e.target.value)}
                  size="small"
                  sx={{ mb: 3, minWidth: 240 }}
                  inputProps={{ min: 0 }}
                />

                <Divider sx={{ mb: 3 }} />

                {/* Opening Chips by Denomination */}
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Opening Chips (by Denomination)
                </Typography>
                <Grid container spacing={2} mb={1}>
                  {chips.map((chip) => {
                    const cc = getChipColor(chip.denomination);
                    return (
                    <Grid item xs={6} sm={4} md={3} key={chip.denomination}>
                      <TextField
                        label={`${chip.denomination} chips`}
                        type="number"
                        value={formChipQty[chip.denomination] ?? ""}
                        onChange={(e) =>
                          setFormChipQty((prev) => ({
                            ...prev,
                            [chip.denomination]: e.target.value,
                          }))
                        }
                        size="small"
                        fullWidth
                        inputProps={{ min: 0 }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderColor: cc.bg },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: cc.bg, borderWidth: 2 },
                          "& .MuiInputLabel-root": { color: cc.bg, fontWeight: 700 },
                        }}
                      />
                    </Grid>
                    );
                  })}
                </Grid>
                <Paper sx={{ px: 2, py: 1, mb: 3, background: "#0f2740", display: "inline-block", borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
                    Total Chips Value: {totalFormChips.toLocaleString()}
                  </Typography>
                </Paper>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button variant="contained" size="large" onClick={handleStartShift}>
                    Confirm Start Shift
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => setShowStartForm(false)}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* ---------------- TELEGRAM CASHIER SHIFTS ---------------- */}
        {tgShifts.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight={600} mb={2}>
              📱 Telegram Cashiers ({tgShifts.filter(s => s.is_active).length} active / {tgShifts.length} total)
            </Typography>
            <Grid container spacing={2} mb={2}>
              {tgShifts.map((s) => (
                <Grid item xs={12} sm={6} md={4} key={String(s.cashier_id)}>
                  <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${s.is_active ? "#a5d6a7" : "#e0e0e0"}`, background: s.is_active ? "#f9fbe7" : "#fafafa" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography fontWeight={700}>{s.cashier_name}</Typography>
                      {/* ACTIVE/INACTIVE from Telegram Cashier Manager */}
                      <Chip label={s.is_active ? "ACTIVE" : "INACTIVE"} color={s.is_active ? "success" : "error"} size="small" sx={{ fontWeight: 600 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      📱 {s.cashier_phone}
                    </Typography>
                    {/* Shift running status — separate from active/inactive */}
                    <Chip
                      label={s.shift_active ? "Shift Running" : "No Shift"}
                      color={s.shift_active ? "primary" : "default"}
                      size="small" variant="outlined"
                      sx={{ mb: 1, fontSize: "0.7rem" }}
                    />
                    {s.shift_active ? (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                          🕐 Started: {new Date(s.started_at).toLocaleString()}
                        </Typography>
                        {/* Opening amounts */}
                        {(s.opening_cash > 0 || s.opening_chips > 0) && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                            {s.opening_cash > 0 && (
                              <Typography variant="caption" sx={{ background: "#241530", px: 1, py: 0.3, borderRadius: 1 }}>
                                💵 Opening Cash: {s.opening_cash.toLocaleString()}
                              </Typography>
                            )}
                            {s.opening_chips > 0 && (
                              <Typography variant="caption" sx={{ background: "#1a1f36", px: 1, py: 0.3, borderRadius: 1 }}>
                                🎴 Opening Chips: {s.opening_chips.toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {/* Refill summary */}
                        {s.refill?.count > 0 && (
                          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                            <Typography variant="caption" sx={{ background: "#0f2a17", color: "#66bb6a", px: 1, py: 0.3, borderRadius: 1 }}>
                              🔄 Refills ({s.refill.count}): Cash {(s.refill.cash||0).toLocaleString()} + Chips {(s.refill.chips||0).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                        {/* Available chips & cash */}
                        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ background: s.available_chips > 0 ? "#e8f5e9" : "#ffebee", color: s.available_chips > 0 ? "#2e7d32" : "#c62828", px: 1, py: 0.3, borderRadius: 1, fontWeight: 600 }}>
                            🎴 Chips: {(s.available_chips || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ background: s.available_cash > 0 ? "#e8f5e9" : "#ffebee", color: s.available_cash > 0 ? "#2e7d32" : "#c62828", px: 1, py: 0.3, borderRadius: 1, fontWeight: 600 }}>
                            💵 Cash: {(s.available_cash || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 1, background: "#0f2740", borderRadius: 1, textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary">Chip Issue</Typography>
                              <Typography variant="body2" fontWeight={700} color="primary.dark">
                                {(s.chip_in?.total || 0).toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{s.chip_in?.count || 0} txns</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 1, background: "#2e2008", borderRadius: 1, textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary">Chip Cash-Out</Typography>
                              <Typography variant="body2" fontWeight={700} color="warning.dark">
                                {(s.chip_out?.total || 0).toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{s.chip_out?.count || 0} txns</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 1, background: s.net_cash >= 0 ? "#e8f5e9" : "#fce4ec", borderRadius: 1, textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary">Net Cash</Typography>
                              <Typography variant="body2" fontWeight={700} color={s.net_cash >= 0 ? "success.dark" : "error.dark"}>
                                {Math.abs(s.net_cash).toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" title={`Opening ${(s.opening_cash||0)+(s.opening_chips||0)} + In ${s.chip_in?.total||0} - Out ${s.chip_out?.total||0}`}>
                                {s.net_cash >= 0 ? "expected in hand" : "deficit"}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </>
                    ) : s.last_shift ? (
                      <Box sx={{ mt: 0.5, p: 1, background: "#1c2433", borderRadius: 1, border: "1px dashed #ccc" }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Last shift: {new Date(s.last_shift.started_at).toLocaleString()} → {new Date(s.last_shift.ended_at).toLocaleString()}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Typography variant="caption" sx={{ background: "#0f2740", px: 1, py: 0.3, borderRadius: 1 }}>
                            Opening Cash: {(s.last_shift.opening_cash || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ background: "#1a1f36", px: 1, py: 0.3, borderRadius: 1 }}>
                            Opening Chips: {(s.last_shift.opening_chips || 0).toLocaleString()}
                          </Typography>
                          {s.last_shift.chip_in !== null && (
                            <Typography variant="caption" sx={{ background: "#0f2a17", color: "#66bb6a", px: 1, py: 0.3, borderRadius: 1, fontWeight: 600 }}>
                              Chip Issue: {(s.last_shift.chip_in || 0).toLocaleString()}
                            </Typography>
                          )}
                          {s.last_shift.chip_out !== null && (
                            <Typography variant="caption" sx={{ background: "#2e2008", color: "#ffb74d", px: 1, py: 0.3, borderRadius: 1, fontWeight: 600 }}>
                              Chip Out: {(s.last_shift.chip_out || 0).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled" display="block">
                        Shift not started yet
                      </Typography>
                    )}
                    {/* Start / End shift buttons */}
                    {s.is_active && (
                      <Box mt={1.5} sx={{ display: "flex", gap: 1 }}>
                        {!s.shift_active ? (
                          <Button size="small" variant="contained" color="primary"
                            onClick={() => {
                              setTgOpeningCash("");
                              setTgOpeningChips("");
                              setTgStartDialog({ cashier_id: String(s.cashier_id), cashier_name: s.cashier_name });
                            }}>
                            Start Shift
                          </Button>
                        ) : (
                          <>
                            <Button size="small" variant="outlined" color="primary"
                              onClick={() => {
                                setTgRefillCash(""); setTgRefillChips(""); setTgRefillNote("");
                                setTgRefillDialog({ cashier_id: String(s.cashier_id), cashier_name: s.cashier_name });
                              }}>
                              + Refill
                            </Button>
                            <Button size="small" variant="outlined" color="error"
                              onClick={async () => {
                                await axiosRequest("POST", "/shift-management/tg-shifts/end", token, { cashier_id: String(s.cashier_id) });
                                fetchTgShifts();
                              }}>
                              End Shift
                            </Button>
                          </>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* ---------------- ACTIVE SHIFT ---------------- */}

        {shiftActive && (
          <>
            <Typography variant="h6" mb={2}>
              Current Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="subtitle2">Winning Balance</Typography>
                  <Typography variant="h5" fontWeight={600} mt={1}>
                    {(userWallet?.winning_balance || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="subtitle2">Promotional Balance</Typography>
                  <Typography variant="h5" fontWeight={600} mt={1}>
                    {(userWallet?.promotional_balance || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="subtitle2">Cash Deposited</Typography>
                  <Typography variant="h5" fontWeight={600} mt={1}>
                    {(userWallet?.cash_deposite || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="subtitle2">Loan Distributed</Typography>
                  <Typography variant="h5" fontWeight={600} mt={1}>
                    {(userWallet?.loan_distributed || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="subtitle2">Float Outstanding</Typography>
                  <Typography variant="h5" fontWeight={600} mt={1}>
                    {(userWallet?.float_outstanding || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="subtitle2">Cash Issued</Typography>
                  <Typography variant="h5" fontWeight={600} mt={1}>
                    {(issueSummary?.cash_issued || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, background: "#1c2433", borderRadius: 2 }}>
                  <Typography variant="subtitle2">Chips Issued</Typography>
                  <Typography variant="h5" fontWeight={600} mt={1}>
                    {(issueSummary?.chips_issued || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            <Divider sx={{ my: 4 }} />
            <div style={{ margin: "26px 0px" }}>
              <Heading>On Table Chips</Heading>
            </div>
            <Paper elevation={4} sx={{ borderRadius: "12px", overflow: "hidden" }}>
              <Table style={{ width: "100%" }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1c2433" }}>
                    <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Denomination</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Total Value</TableCell>
                    {/* <TableCell sx={{ fontWeight: 600 }}>Stock</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell> */}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {Object.entries(shiftSummary?.on_table_chips || {}).map(([denomination, quantity]) => {
                    const value = denomination * quantity;

                    return (
                      <TableRow key={denomination} hover>
                        <TableCell style={{ textAlign: "center" }}>
                          <Typography fontWeight={600}>
                            {denomination}
                          </Typography>
                        </TableCell>

                        <TableCell style={{ textAlign: "center" }}>
                          {quantity}
                        </TableCell>

                        <TableCell style={{ textAlign: "center" }}>
                          {value.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>

            {/* Close Button */}

            {/* ---------------- REFILL HISTORY ---------------- */}
            {shiftSummary?.refills?.count > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>Counter Refill History</Typography>
                  <Button
                    variant="outlined" size="small" startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadRefillCsv(currentShift?._id)}
                  >
                    Download CSV
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#1c2433" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Cash</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Chips</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Chip Denominations</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Added By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shiftSummary.refills.records.map((r) => {
                        const denomObj = r.chip_denominations instanceof Map
                          ? Object.fromEntries(r.chip_denominations)
                          : (r.chip_denominations || {})
                        const denomStr = Object.entries(denomObj)
                          .filter(([, q]) => Number(q) > 0)
                          .sort((a, b) => Number(a[0]) - Number(b[0]))
                          .map(([d, q]) => `${d}×${q}`)
                          .join(" | ")
                        return (
                          <TableRow key={r._id} hover>
                            <TableCell>{new Date(r.date_time).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ color: r.cash_amount > 0 ? "success.main" : "text.disabled", fontWeight: 600 }}>
                              {r.cash_amount > 0 ? `${Number(r.cash_amount).toLocaleString()}` : "—"}
                            </TableCell>
                            <TableCell align="right" sx={{ color: r.total_chips > 0 ? "primary.main" : "text.disabled", fontWeight: 600 }}>
                              {r.total_chips > 0 ? `${Number(r.total_chips).toLocaleString()}` : "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.82rem", color: "text.secondary" }}>{denomStr || "—"}</TableCell>
                            <TableCell sx={{ color: "text.secondary", fontSize: "0.82rem" }}>{r.note || "—"}</TableCell>
                            <TableCell sx={{ fontSize: "0.82rem", color: "text.secondary" }}>{r.email || "—"}</TableCell>
                          </TableRow>
                        )
                      })}
                      <TableRow sx={{ background: "#1c2433" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total Refilled</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "success.main" }}>
                          {Number(shiftSummary.refills.total_cash).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                          {Number(shiftSummary.refills.total_chips).toLocaleString()}
                        </TableCell>
                        <TableCell colSpan={3} />
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </>
            )}

            {/* ---------------- ACTION BUTTONS ---------------- */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
              {isMainAdmin && (
                <Button variant="outlined" color="primary" size="large" onClick={handleOpenRefill}>
                  + Refill Counter
                </Button>
              )}
              <Button variant="contained" color="error" size="large" onClick={handleCloseShift}>
                Close Shift
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* ---------------- REFILL DIALOG ---------------- */}
      <Dialog open={refillOpen} onClose={() => setRefillOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", px: 3, py: 2, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={700}>Refill Counter</Typography>
          <IconButton onClick={() => setRefillOpen(false)} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Cash Refill */}
          <Typography fontWeight={700} mb={1}>Cash Amount</Typography>
          <TextField
            fullWidth size="small" type="number" label="Cash to Add ()"
            value={refillCash} onChange={(e) => setRefillCash(e.target.value)}
            inputProps={{ min: 0 }} sx={{ mb: 3 }}
          />

          {/* Chip Refill */}
          <Typography fontWeight={700} mb={1}>Chip Denominations</Typography>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#1c2433" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Denomination</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Qty to Add</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chips.map((c) => {
                  const cc = getChipColor(c.denomination);
                  const qty = Number(refillChipQty[c.denomination]) || 0;
                  return (
                    <TableRow key={c.denomination} sx={{ backgroundColor: cc.bg + "18" }}>
                      <TableCell>
                        <Box sx={{ display: "inline-block", px: 1.2, py: 0.3, borderRadius: 1.5, bgcolor: cc.bg, color: cc.text, fontWeight: 700, fontSize: "0.82rem" }}>
                          {c.denomination}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 120 }}>
                        <TextField
                          size="small" type="number"
                          value={refillChipQty[c.denomination] ?? ""}
                          onChange={(e) => setRefillChipQty((p) => ({ ...p, [c.denomination]: e.target.value }))}
                          inputProps={{ min: 0 }} sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: qty > 0 ? "primary.main" : "text.disabled" }}>
                        {qty > 0 ? `${(c.denomination * qty).toLocaleString()}` : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {refillTotalChips > 0 && (
                  <TableRow sx={{ background: "#182338" }}>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total Chips</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>{refillTotalChips.toLocaleString()}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Note */}
          <TextField
            fullWidth size="small" label="Note (optional)" multiline rows={2}
            value={refillNote} onChange={(e) => setRefillNote(e.target.value)} sx={{ mb: 3 }}
          />

          {/* Summary */}
          {(Number(refillCash) > 0 || refillTotalChips > 0) && (
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2, background: "linear-gradient(135deg,#11998e,#38ef7d)" }}>
              <Typography fontWeight={700} color="#fff">
                Total Refill: {(Number(refillCash || 0) + refillTotalChips).toLocaleString()}
                &nbsp;(Cash: {Number(refillCash || 0).toLocaleString()} + Chips: {refillTotalChips.toLocaleString()})
              </Typography>
            </Paper>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => setRefillOpen(false)}>Cancel</Button>
            <Button
              variant="contained" color="success"
              disabled={refillSubmitting || (!refillCash && refillTotalChips === 0)}
              onClick={handleSubmitRefill}
            >
              {refillSubmitting ? "Saving…" : "Confirm Refill"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ---------------- TG CASHIER REFILL DIALOG ---------------- */}
      <Dialog open={!!tgRefillDialog} onClose={() => !tgRefillLoading && setTgRefillDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: "linear-gradient(135deg,#11998e,#38ef7d)", px: 3, py: 2, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={700}>Refill — {tgRefillDialog?.cashier_name}</Typography>
          <IconButton onClick={() => setTgRefillDialog(null)} sx={{ color: "#fff" }} disabled={tgRefillLoading}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth size="small" type="number" label="Cash Amount ()"
            value={tgRefillCash} onChange={(e) => setTgRefillCash(e.target.value)}
            inputProps={{ min: 0 }} sx={{ mb: 2 }}
          />
          <TextField
            fullWidth size="small" type="number" label="Chip Value ()"
            value={tgRefillChips} onChange={(e) => setTgRefillChips(e.target.value)}
            inputProps={{ min: 0 }} sx={{ mb: 2 }}
          />
          <TextField
            fullWidth size="small" label="Note (optional)"
            value={tgRefillNote} onChange={(e) => setTgRefillNote(e.target.value)} sx={{ mb: 1 }}
          />
          {(Number(tgRefillCash) > 0 || Number(tgRefillChips) > 0) && (
            <Paper sx={{ p: 1.5, background: "#0f2a17", borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700} color="success.dark">
                Total Refill: {(Number(tgRefillCash||0) + Number(tgRefillChips||0)).toLocaleString()}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setTgRefillDialog(null)} disabled={tgRefillLoading}>Cancel</Button>
          <Button variant="contained" color="success"
            disabled={tgRefillLoading || (!Number(tgRefillCash) && !Number(tgRefillChips))}
            onClick={async () => {
              setTgRefillLoading(true);
              await axiosRequest("POST", "/shift-management/tg-shifts/refill", token, {
                cashier_id:   tgRefillDialog.cashier_id,
                cash_amount:  Number(tgRefillCash  || 0),
                chip_value:   Number(tgRefillChips || 0),
                note:         tgRefillNote,
              });
              setTgRefillLoading(false);
              setTgRefillDialog(null);
              fetchTgShifts();
            }}>
            {tgRefillLoading ? "Saving…" : "Confirm Refill"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- TG CASHIER START SHIFT DIALOG ---------------- */}
      <Dialog open={!!tgStartDialog} onClose={() => !tgStartLoading && setTgStartDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff", fontWeight: 700 }}>
          Start Shift — {tgStartDialog?.cashier_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth size="small" type="number" label="Opening Cash Amount ()"
            value={tgOpeningCash} onChange={(e) => setTgOpeningCash(e.target.value)}
            inputProps={{ min: 0 }} sx={{ mb: 2 }}
          />
          <TextField
            fullWidth size="small" type="number" label="Opening Chips Value ()"
            value={tgOpeningChips} onChange={(e) => setTgOpeningChips(e.target.value)}
            inputProps={{ min: 0 }} sx={{ mb: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setTgStartDialog(null)} disabled={tgStartLoading}>Cancel</Button>
          <Button variant="contained" color="primary" disabled={tgStartLoading}
            onClick={async () => {
              setTgStartLoading(true);
              await axiosRequest("POST", "/shift-management/tg-shifts/start", token, {
                cashier_id:    tgStartDialog.cashier_id,
                opening_cash:  Number(tgOpeningCash  || 0),
                opening_chips: Number(tgOpeningChips || 0),
              });
              setTgStartLoading(false);
              setTgStartDialog(null);
              fetchTgShifts();
            }}>
            {tgStartLoading ? "Starting…" : "Confirm Start Shift"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- CLOSE SHIFT CONFIRMATION ---------------- */}
      <Dialog open={closeConfirmOpen} onClose={() => !closeShiftLoading && setCloseConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: "linear-gradient(135deg,#b71c1c,#e53935)", color: "#fff", fontWeight: 700 }}>
          Close Shift
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Typography variant="body1">
            Are you sure you want to <strong>close the current shift</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setCloseConfirmOpen(false)} disabled={closeShiftLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmCloseShift} disabled={closeShiftLoading}>
            {closeShiftLoading ? "Closing…" : "Yes, Close Shift"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}